import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { prepacksApi } from '../../services/api'
import { toPublicPrepack } from '../../pages/prepacks/prepackCardModel'
import { rankSurface } from '../../pages/prepacks/prepackSurfaceModel'
import {
  brandingToCssVars,
  resolveBrandingWithOverrides,
} from '@feed-widget/branding'
import { loadFonts } from '@feed-widget/utils/fonts'
import { Button, SegmentedControl } from '../common'
import WidgetSurfacePreview, { PREVIEW_VIEWER_TOKEN } from './WidgetSurfacePreview'
import { buildPreviewShowcase } from './previewShowcase'
import { labelForDisplayType } from '../../constants/displayTypes'
import { normalizeFeedPreviewItem } from '../../utils/feedPreviewItem'
import usePreviewInspect from './usePreviewInspect'
import { PreviewInspectStatus, PreviewInspectToggle } from './PreviewInspectControls'
import styles from './SurfacePreviewCanvas.module.css'

const DEVICE_PRESETS = {
  desktop: { width: 720, label: 'Desktop' },
  mobile: { width: 380, label: 'Mobile' },
}

function getSurfaceItems(displayType, showcase) {
  if (displayType === 'carousel' || displayType === 'horizontal_carousel') {
    return showcase.compactItems
  }
  return [...showcase.feedItems, ...showcase.compactItems]
}

function getPreviewProvider(surface, organizationSlug) {
  const configuredProvider = surface?.config?.provider
  if (configuredProvider) return String(configuredProvider).toLowerCase()

  const slug = String(organizationSlug || '').toLowerCase()
  if (slug === 'og') return 'og'
  if (slug === 'cryptodotcom') return 'cryptodotcom'
  return ''
}

function AssignedPrepackPreview({ organizationSlug, surface, branding, displayType, canViewPrepacks }) {
  const library = useQuery({
    queryKey: ['prepacks', 'library', organizationSlug, 'surface', surface.slug],
    queryFn: ({ signal }) => prepacksApi.listSurfacePrepacks(organizationSlug, surface.slug, { signal }),
    enabled: Boolean(organizationSlug && canViewPrepacks && surface.slug),
  })
  const configuration = useQuery({
    queryKey: ['prepacks', 'configuration', organizationSlug],
    queryFn: () => prepacksApi.getConfiguration(organizationSlug),
    enabled: Boolean(organizationSlug && canViewPrepacks),
  })
  const items = useMemo(() => rankSurface(
    (library.data?.items || []).filter((pack) => pack.status === 'published' && pack.surfaces?.includes(surface.slug)),
    { layout_config: surface.config?.prepacks?.layout_config },
  ).map(toPublicPrepack), [library.data, surface])
  const oddsFormat = configuration.data?.odds_format === 'decimal' ? 'decimal' : 'american'
  const templates = configuration.data?.card_templates || []
  const hasTemplates = items.some((pack) => pack.template_id && templates.some((template) => template.id === pack.template_id))

  if (!organizationSlug) return <div className={styles.loading}>Select an organization to preview assigned prepacks.</div>
  if (library.isPending || configuration.isPending) return <div className={styles.loading} role="status">Loading assigned prepacks…</div>
  if (library.isError || configuration.isError) {
    return <div className={styles.loading} role="alert">Assigned prepacks could not be loaded. <Button onClick={() => { library.refetch(); configuration.refetch() }}>Retry</Button></div>
  }
  return (
    <>
      <p className={styles.inspectStatus} role="note">
        Assigned content preview, not live availability. Betting is disabled.
        {hasTemplates ? ' Template variants can override these surface defaults. Edit their artwork, footer and action in Prepacks → Templates.' : ''}
      </p>
      {items.length ? (
        <WidgetSurfacePreview
          branding={branding}
          displayType={displayType}
          surface={surface}
          contentMode="prepacks"
          prepacks={items}
          prepackTemplates={templates}
          oddsFormat={oddsFormat}
        />
      ) : <div className={styles.loading}>No published prepacks are assigned to this surface.</div>}
    </>
  )
}

/**
 * SurfacePreviewCanvas renders the same widget surface components used by
 * production embeds, backed by deterministic preview feed data. Operators can
 * click, scroll, and keyboard through the selected display_type without
 * calling the live Feed API.
 *
 * With "Edit elements" on, the canvas becomes an inspector: widget
 * interactivity is suppressed, hovering outlines the styleable region under
 * the cursor, and clicking resolves the region to its token paths and asks
 * the page (via `onTokenSelect`) to focus them in the inspector.
 */
export default function SurfacePreviewCanvas({
  branding,
  overrides,
  displayType = 'story_block',
  surface = null,
  organizationSlug = '',
  contentMode = 'feed',
  canViewPrepacks = false,
  onContentModeChange,
  inspectTokenPaths = [],
  onTokenSelect,
  flashRequest = null, // { path, nonce } — locate a token's elements in the preview
}) {
  const effectiveContentMode = canViewPrepacks && surface?.prepacks_enabled ? contentMode : 'feed'
  const [device, setDevice] = useState('desktop')
  // Identity-gated affordances (today just the follow button) are invisible to
  // an anonymous viewer, so a token toggled here is the only way to review them
  // on this page. Defaults to anonymous: that is what a signed-out punter sees.
  const [viewer, setViewer] = useState('anonymous')
  const frameRef = useRef(null)

  const resolvedBranding = useMemo(
    () => resolveBrandingWithOverrides(branding, overrides || null),
    [branding, overrides]
  )
  const cssVars = useMemo(() => brandingToCssVars(resolvedBranding), [resolvedBranding])
  useEffect(() => {
    if (resolvedBranding.fontUrls?.length) loadFonts(resolvedBranding.fontUrls)
  }, [resolvedBranding.fontUrls])
  const previewProvider = useMemo(
    () => getPreviewProvider(surface, organizationSlug),
    [surface, organizationSlug]
  )
  const showcase = useMemo(
    () => buildPreviewShowcase(resolvedBranding.buttonText, { provider: previewProvider }),
    [resolvedBranding.buttonText, previewProvider]
  )
  const previewItems = useMemo(
    () => getSurfaceItems(displayType, showcase).map((item) => normalizeFeedPreviewItem(item)),
    [displayType, showcase]
  )
  const activePathSet = useMemo(() => new Set(inspectTokenPaths), [inspectTokenPaths])
  const devicePreset = DEVICE_PRESETS[device]
  // Surface/display/content switches remount the widget tree.
  const { inspectMode, toggleInspectMode, inspectStatus, flashMiss, frameProps } = usePreviewInspect({
    frameRef,
    activePaths: activePathSet,
    onTokenSelect,
    flashRequest,
    resetKey: `${displayType}|${surface?.id}|${effectiveContentMode}`,
  })

  return (
    <div className={styles.canvas} data-testid="surface-preview-canvas">
      <div className={styles.canvasToolbar}>
        <SegmentedControl
          className={styles.deviceControl}
          items={[
            { value: 'desktop', label: 'Desktop' },
            { value: 'mobile', label: 'Mobile' },
          ]}
          value={device}
          onValueChange={setDevice}
          label="Preview viewport"
        />
        {canViewPrepacks && surface?.prepacks_enabled && (
          <SegmentedControl
            items={[{ value: 'feed', label: 'Feed' }, { value: 'prepacks', label: 'Prepacks' }]}
            value={effectiveContentMode}
            onValueChange={onContentModeChange}
            label="Preview content"
          />
        )}
        {effectiveContentMode !== 'prepacks' && (
        <SegmentedControl
          className={styles.viewerControl}
          items={[
            { value: 'anonymous', label: 'Anonymous' },
            { value: 'identified', label: 'Identified' },
          ]}
          value={viewer}
          onValueChange={setViewer}
          label="Preview viewer"
        />
        )}
        <PreviewInspectToggle inspectMode={inspectMode} onToggle={toggleInspectMode} />
      </div>

      <PreviewInspectStatus
        inspectMode={inspectMode}
        inspectStatus={inspectStatus}
        flashMiss={flashMiss}
        idleText={`${labelForDisplayType(displayType)} · ${surface ? surface.name : 'Shared branding'}`}
      />

      <div className={styles.frameScroll} data-testid={`preview-frame-${displayType}`}>
        <div
          ref={frameRef}
          role="region"
          aria-label="Widget preview"
          className={[styles.frame, inspectMode ? styles.frameInspecting : ''].filter(Boolean).join(' ')}
          style={{ ...cssVars, width: `${devicePreset.width}px` }}
          data-dark-mode={resolvedBranding.darkMode ? 'true' : 'false'}
          {...frameProps}
        >
          {effectiveContentMode === 'prepacks' ? (
            <AssignedPrepackPreview
              organizationSlug={organizationSlug}
              surface={surface}
              branding={resolvedBranding}
              displayType={displayType}
              canViewPrepacks={canViewPrepacks}
            />
          ) : (
          <WidgetSurfacePreview
            branding={resolvedBranding}
            displayType={displayType}
            surface={surface}
            organizationSlug={organizationSlug}
            items={previewItems}
            viewerToken={viewer === 'identified' ? PREVIEW_VIEWER_TOKEN : null}
            fallback={<div className={styles.loading}>Loading preview...</div>}
          />
          )}
        </div>
      </div>
    </div>
  )
}
